// Einstellungen: Konto, Darstellung, Postfächer & Signaturen, Regeln (2.C). Bob-Budget usw. folgen.
import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';
import { toast } from '../components/Toast.tsx';
import { RegelnSettings } from '../components/Regeln.tsx';
import { KalenderSettings } from '../components/KalenderSettings.tsx';
import { FragenDerWoche } from '../components/FragenDerWoche.tsx';
import { PushSettings } from '../components/PushSettings.tsx';
import { KontoSettings } from '../components/KontoSettings.tsx';
import { Lernschleife } from '../components/Lernschleife.tsx';
import {
  PostfachBearbeiten,
  PostfachHinzufuegen,
  PostfachStatus,
} from '../components/PostfachSettings.tsx';

export function SettingsPage() {
  const { me, mailboxes, signatures } = useStore((s) => ({
    me: s.me,
    mailboxes: s.mailboxes,
    signatures: s.signatures,
  }));
  const [theme, setTheme] = useState(() => document.documentElement.dataset['theme'] ?? 'auto');
  const [sig, setSig] = useState<Record<number, string>>({});
  useEffect(() => {
    setSig(Object.fromEntries(signatures.map((s) => [s.id, s.html])));
  }, [signatures]);
  const applyTheme = (t: string) => {
    setTheme(t);
    if (t === 'auto') delete document.documentElement.dataset['theme'];
    else document.documentElement.dataset['theme'] = t;
    try {
      localStorage.setItem('mm.theme', t);
    } catch {
      /* egal */
    }
  };
  return (
    <section className="page-body" aria-label="Einstellungen">
      <div className="s-wrap">
        <h2 style={{ margin: 0 }}>Einstellungen</h2>
        <div className="s-hit" style={{ cursor: 'default' }}>
          <div className="s-subj">Mein Konto</div>
          <div className="s-snip">
            Angemeldet als <b>{me?.name}</b> ({me?.email}) · Rolle {me?.role} · Anmeldung über{' '}
            {me?.via === 'crm' ? 'die CRM-Sitzung' : 'die Mail-App'}
          </div>
          <div>
            <button
              className="btn sm"
              onClick={() => void api.logout().then(() => location.reload())}
            >
              Abmelden
            </button>
          </div>
          <KontoSettings />
        </div>
        <div className="s-hit" style={{ cursor: 'default' }}>
          <div className="s-subj">Darstellung</div>
          <div className="s-snip" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            Farbschema
            <select
              value={theme}
              onChange={(e) => applyTheme(e.target.value)}
              style={{ font: 'inherit', padding: 4, borderRadius: 8 }}
            >
              <option value="auto">wie das System</option>
              <option value="light">hell</option>
              <option value="dark">dunkel</option>
            </select>
          </div>
          <div className="s-snip">
            Tastenkürzel: <kbd>j</kbd>/<kbd>k</kbd> nächste/vorige Mail · <kbd>r</kbd> antworten ·{' '}
            <kbd>a</kbd> allen · <kbd>f</kbd> weiterleiten · <kbd>e</kbd> erledigt · <kbd>u</kbd>{' '}
            ungelesen · <kbd>c</kbd> verfassen · <kbd>/</kbd> Liste durchsuchen · <kbd>⌘F</kbd>{' '}
            Suche
          </div>
        </div>
        <div className="s-hit" style={{ cursor: 'default' }}>
          <div className="s-subj">Postfächer & Signaturen</div>
          {mailboxes.map((mb) => {
            const s = signatures.find((x) => x.mailboxId === mb.id);
            return (
              <div
                key={mb.id}
                style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 6 }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className={`dot c-${mb.color}`} />
                  <b>{mb.address}</b>
                  <span className="muted">
                    {mb.company === 'hoerthin' ? 'Hört Hin! GmbH' : 'Du bist die Musik UG'} ·{' '}
                    {mb.visibility === 'personal' ? 'persönlich' : 'gemeinsam'} ·{' '}
                    <PostfachStatus mb={mb} />
                  </span>
                </div>
                <PostfachBearbeiten mb={mb} />
                {s && (
                  <div style={{ marginTop: 8 }}>
                    <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                      Deine Signatur (HTML)
                    </div>
                    <textarea
                      value={sig[s.id] ?? ''}
                      onChange={(e) => setSig({ ...sig, [s.id]: e.target.value })}
                      rows={5}
                      style={{
                        width: '100%',
                        font: 'inherit',
                        fontSize: 12.5,
                        borderRadius: 10,
                        border: '1px solid var(--line)',
                        padding: 8,
                        background: 'var(--bg-surface)',
                        color: 'var(--fg)',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                      <button
                        className="btn sm"
                        disabled={(sig[s.id] ?? '') === s.html}
                        onClick={() =>
                          void api
                            .signatureSave(s.id, sig[s.id] ?? '')
                            .then(() => toast('Signatur gespeichert'))
                        }
                      >
                        Speichern
                      </button>
                      <div
                        className="muted"
                        style={{ fontSize: 12 }}
                        dangerouslySetInnerHTML={{ __html: (sig[s.id] ?? '').slice(0, 4000) }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 6 }}>
            <PostfachHinzufuegen />
          </div>
        </div>
        <div className="s-hit" style={{ cursor: 'default' }} id="regeln">
          <div className="s-subj">Regeln</div>
          <div className="s-snip">
            „Wenn … dann …“ für jede neue Mail, vor dem Aussortieren und vor Bob. Neue Regeln
            starten pausiert: erst Probelauf, dann einschalten.
          </div>
          <RegelnSettings />
        </div>
        <div className="s-hit" style={{ cursor: 'default' }} id="benachrichtigungen">
          <div className="s-subj">Benachrichtigungen</div>
          <PushSettings />
        </div>
        <div className="s-hit" style={{ cursor: 'default' }} id="fragen">
          <div className="s-subj">Fragen der Woche</div>
          <div className="s-snip">
            Bob gruppiert die Fragen der letzten 7 Tage nach Themen und prüft, ob das Handbuch sie
            schon beantwortet. Läuft montags früh automatisch.
          </div>
          <FragenDerWoche admin={me?.role === 'admin'} />
        </div>
        {me?.role === 'admin' && (
          <div className="s-hit" style={{ cursor: 'default' }} id="lernschleife">
            <div className="s-subj">Lernschleife</div>
            <div className="s-snip">
              Bob vergleicht seine Entwürfe mit dem, was wirklich rausging, und schlägt neue oder
              geänderte Handbuch-Kapitel vor. Läuft montags früh.
            </div>
            <Lernschleife />
          </div>
        )}
        <div className="s-hit" style={{ cursor: 'default' }} id="kalender">
          <div className="s-subj">Kalender</div>
          <KalenderSettings />
        </div>
        <p className="muted" style={{ fontSize: 12.5 }}>
          Bob-Budget und Telefon folgen.
        </p>
      </div>
    </section>
  );
}
